export interface ImportSourceContext {
  /** User-provided import URL (URL import). */
  inputUrl?: string;
  /** Original filename (e.g. `banana-bread.json`). */
  filename?: string;
  /** Raw file text for URL scanning (JSON, HTML, plain text). */
  fileText?: string;
}

export interface ResolvedImportSource {
  sourceType: 'url' | 'text' | 'none';
  sourceUrl: string;
  sourceText: string;
}

/**
 * Resolve recipe provenance for imports.
 *
 * Priority:
 * 1. Explicit input URL
 * 2. Parsed URL from structured data (schema.org, LLM)
 * 3. URLs found in file content
 * 4. Base domain inferred from a found URL or filename
 * 5. Text fallback describing the import from its filename
 * 6. Empty (sourceType `none`) when even the filename is unknown
 */
export function resolveImportSourceUrl(
  context: ImportSourceContext & { parsedUrl?: string },
): ResolvedImportSource {
  const input = normalizeHttpUrl(context.inputUrl);
  if (input != null) return urlSource(input);

  const parsed = normalizeHttpUrl(context.parsedUrl);
  if (parsed != null) return urlSource(parsed);

  const fromFile = inferUrlFromFile(context.fileText, context.filename);
  if (fromFile != null) return urlSource(fromFile);

  const baseDomain = inferBaseDomainFromFile(context.fileText, context.filename);
  if (baseDomain != null) return urlSource(baseDomain);

  const filename = context.filename?.trim();
  if (filename != null && filename !== '') {
    return { sourceType: 'text', sourceUrl: '', sourceText: `Imported from ${filename}` };
  }

  return { sourceType: 'none', sourceUrl: '', sourceText: '' };
}

function urlSource(url: string): ResolvedImportSource {
  return { sourceType: 'url', sourceUrl: url, sourceText: '' };
}

/** Normalize a candidate URL, or return null when invalid. */
export function normalizeHttpUrl(raw: string | undefined): string | null {
  if (raw == null) return null;
  const trimmed = raw.trim();
  if (trimmed === '') return null;

  try {
    const withScheme = /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
    const url = new URL(withScheme);
    if (url.protocol !== 'http:' && url.protocol !== 'https:') return null;
    if (url.hostname === '') return null;
    return url.href;
  } catch {
    return null;
  }
}

/** Origin only (`https://example.com/`) for a valid http(s) URL. */
export function baseDomainUrl(raw: string | undefined): string | null {
  const normalized = normalizeHttpUrl(raw);
  if (normalized == null) return null;
  try {
    const url = new URL(normalized);
    return `${url.protocol}//${url.hostname}/`;
  } catch {
    return null;
  }
}

function inferUrlFromFile(fileText?: string, filename?: string): string | null {
  const candidates = [
    ...findUrlsInText(fileText ?? ''),
    ...findUrlsInText(filename ?? ''),
  ];
  for (const candidate of candidates) {
    const normalized = normalizeHttpUrl(candidate);
    if (normalized != null) return normalized;
  }
  return null;
}

function inferBaseDomainFromFile(fileText?: string, filename?: string): string | null {
  for (const candidate of findUrlsInText(fileText ?? '')) {
    const base = baseDomainUrl(candidate);
    if (base != null) return base;
  }

  if (filename != null && filename !== '') {
    const fromFilename = inferDomainFromFilename(filename);
    if (fromFilename != null) return fromFilename;
  }

  for (const candidate of findDomainLikeStrings(fileText ?? '')) {
    const base = baseDomainUrl(candidate);
    if (base != null) return base;
  }

  return null;
}

function inferDomainFromFilename(filename: string): string | null {
  const stripped = stripImportFilename(filename);
  const parts = stripped.split(/[-_]/);
  for (let i = parts.length - 1; i >= 0; i -= 1) {
    const part = parts[i];
    if (part == null || !part.includes('.')) continue;
    const base = baseDomainUrl(part);
    if (base != null) return base;
  }
  return null;
}

function stripImportFilename(filename: string): string {
  return filename.replace(
    /\.(json|jsonld|x?html?|docx|odt|doc|pdf|txt|md|markdown|png|jpe?g|webp|avif|heic|heif)$/i,
    '',
  );
}

const URL_IN_TEXT_RE =
  /https?:\/\/[^\s<>"')\]]+/gi;

function findUrlsInText(text: string): string[] {
  if (text === '') return [];
  const matches = text.match(URL_IN_TEXT_RE) ?? [];
  const out: string[] = [];
  const seen = new Set<string>();
  for (const match of matches) {
    const cleaned = match.replace(/[.,;:!?)]+$/, '');
    if (seen.has(cleaned)) continue;
    seen.add(cleaned);
    out.push(cleaned);
  }
  return out;
}

/** Hostname-like tokens such as `allrecipes.com` embedded in filenames or text. */
const DOMAIN_LIKE_RE =
  /\b(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,}\b/gi;

function findDomainLikeStrings(text: string): string[] {
  if (text === '') return [];
  const matches = text.match(DOMAIN_LIKE_RE) ?? [];
  const out: string[] = [];
  const seen = new Set<string>();
  for (const match of matches) {
    const lower = match.toLowerCase();
    if (seen.has(lower)) continue;
    seen.add(lower);
    out.push(lower);
  }
  return out;
}
